// ============================================================
// CNPJ — a definição ÚNICA de "estes dois cadastros são a mesma empresa".
//
// Alimenta a coluna espelho `customers.cnpj_normalized` (índice único, migration
// 0022) e a exibição formatada na interface. Uma definição, dois consumidores:
// se a regra vivesse também dentro do índice SQL, as duas poderiam divergir em
// silêncio — o índice recusando um cadastro que a API julgou válido. É o mesmo
// desenho de `professional-identity.ts` (migration 0020).
//
// Valor ausente/vazio normaliza para `null`, e `null` NUNCA colide: no SQLite os
// NULLs são distintos entre si num índice único, então vários clientes sem CNPJ
// convivem sem problema.
// ============================================================

/** Comprimento canônico do CNPJ: 12 dígitos de base + 2 verificadores. */
const CNPJ_LENGTH = 14
const CNPJ_BASE_LENGTH = 12

/**
 * Pesos do módulo 11, na ordem em que percorrem os dígitos. O segundo
 * verificador usa um dígito a mais (o primeiro verificador já calculado), por
 * isso a sua lista começa em 6.
 */
const FIRST_CHECK_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const
const SECOND_CHECK_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const

/**
 * CNPJ reduzido a só dígitos — a chave de duplicidade. '11.222.333/0001-81',
 * '11222333000181' e ' 11.222.333/0001-81 ' produzem a mesma chave.
 *
 * NÃO valida: normalizar e validar são passos separados de propósito. A coluna
 * espelho aceita o que o cadastro aceitou, e quem decide se aceita é
 * `isValidCnpj` (no Zod, antes de chegar aqui).
 */
export function normalizeCnpj(raw: string | null | undefined): string | null {
  const digits = raw?.replace(/\D/g, '') ?? ''
  return digits === '' ? null : digits
}

/**
 * Dígito verificador pelo módulo 11 da Receita Federal: soma ponderada dos
 * dígitos, resto da divisão por 11, e o dígito é `11 - resto` — exceto quando o
 * resto é 0 ou 1, caso em que o dígito é 0 (senão daria 11 ou 10, que não cabem
 * numa casa).
 */
function checkDigit(digits: readonly number[], weights: readonly number[]): number {
  const sum = digits.reduce((acc, digit, i) => acc + digit * (weights[i] ?? 0), 0)
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

/**
 * CNPJ válido = 14 dígitos, não é uma sequência repetida, e os dois últimos
 * dígitos conferem com o cálculo do módulo 11 sobre os 12 primeiros.
 *
 * A rejeição das sequências repetidas ('00000000000000', '11111111111111', …) é
 * necessária porque elas PASSAM na aritmética do módulo 11 — são o buraco
 * clássico do algoritmo, e o valor mais provável de alguém digitar para "furar"
 * um campo obrigatório.
 */
export function isValidCnpj(raw: string | null | undefined): boolean {
  const normalized = normalizeCnpj(raw)
  if (normalized === null || normalized.length !== CNPJ_LENGTH) return false

  // Todos os dígitos iguais: aritmeticamente válido, cadastralmente falso.
  if (/^(\d)\1{13}$/.test(normalized)) return false

  const digits = normalized.split('').map(Number)
  const base = digits.slice(0, CNPJ_BASE_LENGTH)

  const first = checkDigit(base, FIRST_CHECK_WEIGHTS)
  if (first !== digits[CNPJ_BASE_LENGTH]) return false

  const second = checkDigit([...base, first], SECOND_CHECK_WEIGHTS)
  return second === digits[CNPJ_BASE_LENGTH + 1]
}

/**
 * CNPJ na máscara oficial '00.000.000/0000-00'.
 *
 * O que não normalizar para 14 dígitos volta como veio (aparado) em vez de
 * virar uma máscara truncada: um valor parcial digitado pelo usuário deve ser
 * devolvido tal como ele o escreveu, não mutilado por uma máscara que não se
 * aplica. Mesma postura de `linkedinProfileUrl` em `professional-identity.ts`.
 */
export function formatCnpj(raw: string | null | undefined): string {
  const trimmed = raw?.trim() ?? ''
  const normalized = normalizeCnpj(trimmed)
  if (normalized === null || normalized.length !== CNPJ_LENGTH) return trimmed

  return normalized.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}
